import { getKvpsDealerId, type ThingName, ThingNameSchema } from '@org-avp/device-models';
import type { ILogLayer, LogLayerContext, LogLayerPlugin } from '@org-avp/logger/otel-layer';
import { type Brand, getBrand, getMarket, type KvpsDealerId, type Market, Stage } from '@org-avp/models';

type DistEndpoint = {
  /** Base URL (e.g. `https://telemetry.dist.o11y.develop.avp.tech`). Append `/v1/traces`, `/v1/metrics`, or `/v1/logs`. */
  baseUrl: string;
  /** Sent as `X-API-Key` header (not `Api-Key` like old stack). */
  apiKey: string;
};

/**
 * New dist tenant per-stage OTLP endpoint configuration.
 *
 * @example
 * ```ts
 * const config = DIST_ENDPOINTS[Stage.DEVELOP];
 * const traceUrl = `${config.baseUrl}/v1/traces`;
 * ```
 */
export const DIST_ENDPOINTS: Record<Stage, DistEndpoint> = {
  [Stage.DEVELOP]: {
    baseUrl: 'https://telemetry.dist.o11y.develop.avp.tech',
    apiKey: 'mZVpsdAdq4D2EDScCWfncgym!b9XrKK!t^^XoJfWbWJpLaJM4DqgWpyEy9QJyjSr',
  },
  [Stage.PREPROD]: {
    baseUrl: 'https://telemetry.dist.o11y.preprod.avp.tech',
    apiKey: 'somZfXeH^!nDAcPqdmHbtW3CLVtzUoKKJiv2Vz374ZRa4vQ222ZdAcKzHhjaozhs',
  },
  [Stage.PROD]: {
    baseUrl: 'https://telemetry.dist.o11y.avp.tech',
    apiKey: 'vAJXXjQm9WTrg!Ndk3EYn2ELT5Luf7ymqxmNjY357sq7ZRXvbZaSa^xsC!KfEuk5',
  },
};

/**
 * Derive a low-cardinality span name from an HTTP method + URL.
 * Dynamic path segments are collapsed so that traces group by route
 * rather than by individual resource.
 *
 * Replacement order matters — ThingNames must be matched before bare UUIDs,
 * and dealer IDs before numeric segments.
 */
export function spanNameFromUrl(method: string, url: string): string {
  try {
    const pathname = new URL(url, globalThis.location?.origin).pathname;
    const normalized = pathname
      .replace(/\/[A-Z]{3}\d{5}[A-Z]_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{thingName}')
      .replace(/\/[A-Z]{3}\d{5}[A-Z]/g, '/{dealerId}')
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{id}')
      .replace(/\/[0-9a-f]{20,}/gi, '/{id}')
      .replace(/\/\d+/g, '/{id}');
    return `${method.toUpperCase()} ${normalized}`;
  } catch {
    return `${method.toUpperCase()}`;
  }
}

/**
 * Creates a LogLayer plugin that enriches logs with dealerId, brand, and market if thingName is given.
 * Parses and caches enrichment when context is set, then injects cached data at log time.
 */
export function enrichThingContextPlugin(): LogLayerPlugin {
  const cache = new Map<
    string,
    { thingName: ThingName; dealerId: KvpsDealerId; brand: Brand; market: Market } | null
  >();

  return {
    id: 'enrich-thing-context',
    onContextCalled(context: LogLayerContext, _logLayer: ILogLayer) {
      const rawThingName = context?.thingName;
      if (!rawThingName || typeof rawThingName !== 'string') return context;

      if (cache.has(rawThingName)) return context;

      const parsedThingName = ThingNameSchema.safeParse(rawThingName);
      if (!parsedThingName.success) {
        cache.set(rawThingName, null);
        return context;
      }

      const dealerId = getKvpsDealerId(parsedThingName.data);
      const brand = getBrand(dealerId);
      const market = getMarket(dealerId);

      cache.set(rawThingName, {
        thingName: parsedThingName.data,
        dealerId,
        brand,
        market,
      });

      return context;
    },
    // biome-ignore lint/suspicious/noExplicitAny: yeah, we need to re-export them in the logger
    onBeforeDataOut({ data, context }: any, _logLayer: ILogLayer) {
      const rawThingName = context?.thingName;
      if (!rawThingName || typeof rawThingName !== 'string') return data;

      const cached = cache.get(rawThingName);
      if (!cached) return data;

      return { ...(data || {}), ctx: { ...context, ...cached } };
    },
  };
}
